import {Component, effect, inject, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {TableModule} from 'primeng/table';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {Button} from 'primeng/button';
import {
  ProviderContractInvoiceResponse,
  AdminProviderContractControllerService,
  AppDatePipe,
} from '@frontend/shared';
import {finalize, map, switchMap, tap} from 'rxjs/operators';
import {BehaviorSubject} from 'rxjs';
import {toSignal} from '@angular/core/rxjs-interop';
import {BackgroundCardComponent} from '../../../../shared/components/titled-background-card/background-card.component';
import {LoadingComponent} from '../../../../shared/components/loading/loading.component';
import {NoDataComponent} from '../../../../shared/components/no-data/no-data.component';
import {ProviderInvoiceUploadDialogComponent} from './provider-invoice-upload-dialog/provider-invoice-upload-dialog.component';
import {TableColumnFilterComponent} from '../../../../shared/components/table/table-column-filter/table-column-filter.component';
import {ToastService} from '@frontend/toast';
import {ProviderInvoiceTableData} from './provider-invoice-table.data';
import {GlobalSearchComponent} from '../../../../shared/components/search/global-search/global-search.component';

export interface ProviderInvoiceTableColumn {
  field: string;
  headerKey: string;
  testId: string;
  columnWidth: string;
  sortableColumn?: string;
  filterable: boolean;
}

@Component({
  selector: 'app-selecthor-admin-provider-invoices-list',
  templateUrl: './selecthor-admin-provider-invoices.component.html',
  imports: [
    FormsModule,
    TableModule,
    TranslocoPipe,
    BackgroundCardComponent,
    LoadingComponent,
    NoDataComponent,
    Button,
    ProviderInvoiceUploadDialogComponent,
    TableColumnFilterComponent,
    GlobalSearchComponent,
    AppDatePipe,
  ],
})
export class SelecthorAdminProviderInvoicesComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly adminProviderContractController = inject(AdminProviderContractControllerService);
  private readonly toastService = inject(ToastService);

  constructor() {
    effect(() => {
      // Whenever the dialog is closed, reset the selected invoice.
      // This ensures that clicking 'Edit' on the same row again
      // will be seen as a value change (null -> object).
      if (!this.createInvoiceDialogVisible()) {
        this.selectedInvoice.set(null);
      }
    });
  }

  readonly loading = signal(false);
  private readonly refreshTrigger$ = new BehaviorSubject<void>(undefined);

  // 1. Pipe the trigger into the API call
  private readonly invoices$ = this.refreshTrigger$.pipe(
    tap(() => this.loading.set(true)),
    switchMap(() =>
      this.adminProviderContractController.loadAllInvoices().pipe(
        // Catch errors inside switchMap so the main stream doesn't die
        finalize(() => this.loading.set(false)),
      ),
    ),
    map((items) => (items ?? []).map((item) => this.toInvoiceTableData(item))),
  );

  // 2. The signal now automatically reacts to the refreshTrigger$
  readonly invoiceRows = toSignal(this.invoices$, {initialValue: []});

  protected refreshData(): void {
    this.refreshTrigger$.next();
  }

  private toInvoiceTableData(x: ProviderContractInvoiceResponse): ProviderInvoiceTableData {
    return {
      invoiceNumber: x.invoiceNumber ?? '',
      invoiceDate: x.invoiceDate ? new Date(x.invoiceDate) : null,
      invoiceAmount: x.invoiceAmount ?? null,
      currency: x.currency ? this.transloco.translate(x.currency) : '',
      status: x.status
        ? this.transloco.translate('selecthorAdmin.provider.invoices.statusValue.' + x.status) || ''
        : '',
      dueDate: x.dueDate ? new Date(x.dueDate) : null,
      provider: x.provider?.name || '',
      plan: x.paymentPlan
        ? x.paymentPlan.translations
            ?.filter((translation) => translation.language === this.transloco.getActiveLang())
            .map((translation) => translation.name)[0] || ''
        : '',
      description: x.description ?? null,
      originalData: x,
    };
  }

  protected createInvoiceDialogVisible = signal(false);
  protected selectedInvoice = signal<ProviderContractInvoiceResponse | null>(null);

  readonly columns: ProviderInvoiceTableColumn[] = [
    {
      field: 'invoiceNumber',
      headerKey: 'selecthorAdmin.provider.invoices.invoiceId',
      testId: 'invoice-number',
      sortableColumn: 'invoiceNumber',
      columnWidth: '15%',
      filterable: true,
    },
    {
      field: 'invoiceDate',
      headerKey: 'selecthorAdmin.provider.invoices.invoiceDate',
      testId: 'invoice-date',
      sortableColumn: 'invoiceDate',
      columnWidth: '10%',
      filterable: false,
    },
    {
      field: 'invoiceAmount',
      headerKey: 'selecthorAdmin.provider.invoices.invoiceAmount',
      testId: 'invoice-amount',
      sortableColumn: 'invoiceAmount',
      columnWidth: '10%',
      filterable: false,
    },
    {
      field: 'status',
      headerKey: 'selecthorAdmin.provider.invoices.status',
      testId: 'status',
      sortableColumn: 'status',
      columnWidth: '10%',
      filterable: true,
    },
    {
      field: 'dueDate',
      headerKey: 'selecthorAdmin.provider.invoices.dueDate',
      testId: 'dueDate',
      sortableColumn: 'dueDate',
      columnWidth: '10%',
      filterable: false,
    },
    {
      field: 'provider',
      headerKey: 'selecthorAdmin.provider.invoices.provider',
      testId: 'provider',
      sortableColumn: 'provider',
      columnWidth: '10%',
      filterable: true,
    },
    {
      field: 'plan',
      headerKey: 'selecthorAdmin.provider.invoices.plan',
      testId: 'plan',
      sortableColumn: 'plan',
      columnWidth: '10%',
      filterable: true,
    },
    {
      field: 'description',
      headerKey: 'selecthorAdmin.provider.invoices.description',
      testId: 'description',
      sortableColumn: 'description',
      columnWidth: '20%',
      filterable: true,
    },
    {
      field: 'actions', // Changed from download
      headerKey: 'selecthorAdmin.provider.invoices.table.actions',
      testId: 'actions',
      columnWidth: '15%',
      filterable: false,
    },
  ];

  readonly globalFilterFields = [
    'invoiceNumber',
    'plan',
    'description',
    'status',
    'amount',
    'provider',
  ];

  editInvoice(invoice: ProviderContractInvoiceResponse): void {
    this.selectedInvoice.set(invoice);
    this.createInvoiceDialogVisible.set(true);
  }

  deleteInvoice(invoice: ProviderContractInvoiceResponse): void {
    const invoiceUuid = invoice.invoiceUuid;
    const contractUuid = invoice.contractUuid;
    const tenantId = invoice.tenantId;

    if (!invoiceUuid || !contractUuid || !tenantId) {
      this.toastService.showError(
        'common.error',
        'selecthorAdmin.provider.invoices.delete.error.missingData',
      );
      return;
    }

    if (confirm(this.transloco.translate('selecthorAdmin.provider.invoices.delete.confirmation'))) {
      this.loading.set(true);
      this.adminProviderContractController
        .deleteInvoice({
          invoiceUuid,
        })
        .pipe(finalize(() => this.loading.set(false)))
        .subscribe({
          next: () => {
            this.toastService.showSuccess('common.success', 'common.delete.success');
            this.refreshData();
          },
          error: (err: any) => {
            console.error('Delete failed', err);
            this.toastService.showError(
              'common.error',
              'selecthorAdmin.provider.invoices.delete.error.failed',
            );
          },
        });
    }
  }

  downloadInvoice(invoice: ProviderContractInvoiceResponse): void {
    // 1. Check if we have the necessary IDs
    const invoiceUuid = invoice.invoiceUuid;
    const contractUuid = invoice.contractUuid;
    const tenantId = invoice.tenantId;

    if (!invoiceUuid || !contractUuid || !tenantId) {
      this.toastService.showError(
        'common.error',
        'selecthorAdmin.provider.invoices.download.error.missingData',
      );
      return;
    }

    // 2. You MUST subscribe to the observable to trigger the request
    this.adminProviderContractController
      .downloadProviderInvoice1({
        invoiceUuid: invoiceUuid,
        contractUuid: contractUuid,
        tenantId: String(tenantId),
      })
      .subscribe({
        next: (blob: Blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          // Try to use a sensible filename
          link.download = `Invoice_${invoice.invoiceNumber || invoiceUuid}.pdf`;
          link.click();

          // Cleanup
          window.URL.revokeObjectURL(url);
          link.remove();
        },
        error: (err: any) => {
          console.error('Download failed', err);
          this.toastService.showError(
            'common.error',
            'selecthorAdmin.provider.invoices.download.error.failed',
          );
        },
      });
  }

  viewInvoiceData(): void {
    this.selectedInvoice.set(null);
    this.createInvoiceDialogVisible.set(true);
  }

  onInvoiceSaved() {
    this.toastService.showSuccess(
      'common.save.success.header',
      'selecthorAdmin.provider.invoices.saved.success.detail',
    );
    this.refreshData();
  }
}
